import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faDownload,
    faReceipt,
    faChevronLeft,
    faChevronRight,
    faCalendarAlt,
    faFileInvoice,
} from "@fortawesome/free-solid-svg-icons";
import { Row, Col, Card, Table, Badge, InputGroup, Form, Button } from "@themesberg/react-bootstrap";
import { downloadInvoice, userMyPaymentsData } from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
    background: "linear-gradient(90deg, #2c60ba 0%, #005dfb 100%)",
    border: "none",
    borderRadius: "0.5rem",
    color: "#ffffff",
};

const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `₹${num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatDate = (value) => {
    if (!value) return "N/A" || "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const formatStatus = (value) => {
    if (!value) return "N/A" || "-";
    const str = String(value).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getStatusBadgeStyle = (status) => {
    switch (String(status || "").toLowerCase()) {
        case "success":
            return { background: "#10b981", color: "#ffffff" };
        case "completed":
            return { background: "#10b981", color: "#ffffff" };
        case "pending":
            return { background: "#f59e0b", color: "#ffffff" };
        case "failed":
            return { background: "#ef4444", color: "#ffffff" };
        case "refunded":
            return { background: "#6366f1", color: "#ffffff" };
        default:
            return { background: "#6b7280", color: "#ffffff" };
    }
};

const capitalize = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const mapPaymentItem = (item) => ({
    //   apiId: item?.transaction_id ?? null,
    transactionId: String(item?.transaction_id ?? ""),
    date: item?.transaction_date || "N/A" || "-",
    course: capitalize(item?.course || "N/A" || "-"),
    amount: Number(item?.amount || 0),
    type: capitalize(item?.type || "N/A" || "-"),
    paymentMethod: capitalize(item?.payment_method || "N/A" || "-"),
    status: item?.status || "",
    invoiceUrl: item?.invoice_url || item?.invoice || null,
    documentAvailable: item?.document_available ?? false,
    documentType: item?.document_type || null,
});

export default function UserMyPayments() {
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const searchDebounceRef = useRef(null);
    const firstSearchRenderRef = useRef(true);

    const fetchPayments = async (page = 1, perPage = 10, searchTerm = "") => {
        try {
            setLoading(true);

            const response = await userMyPaymentsData({
                page,
                per_page: perPage,
                search: searchTerm,
            });

            const responseData = response?.data || {};
            const apiData = responseData?.data || {};
            const apiTransactions = Array.isArray(apiData?.transactions)
                ? apiData.transactions
                : [];
            const apiPagination = apiData?.pagination || {};

            setTransactions(apiTransactions.map(mapPaymentItem));
            setPagination({
                current_page: Number(apiPagination.current_page ?? page) || page,
                last_page: Number(apiPagination.last_page ?? 1) || 1,
                per_page: Number(apiPagination.per_page ?? perPage) || perPage,
                total: Number(apiPagination.total ?? apiTransactions.length) || apiTransactions.length,
            });
        } catch (error) {
            console.error("Failed to fetch my payments:", error);
            toast.error(error?.response?.data?.message || "Unable to load my payments.");
            setTransactions([]);
            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPayments(1, 10, "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (firstSearchRenderRef.current) {
            firstSearchRenderRef.current = false;
            return;
        }

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            fetchPayments(1, pagination.per_page, search.trim());
        }, 300);

        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const clearSearchDebounce = () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
    };


    const getShowingRange = () => {
        const total = pagination.total || 0;
        const current = pagination.current_page || 1;
        const perPage = pagination.per_page || 10;

        if (total === 0) {
            return { start: 0, end: 0 };
        }

        const start = (current - 1) * perPage + 1;
        const end = Math.min(current * perPage, total);

        return { start, end };
    };

    const getPageItems = () => {
        const totalPages = pagination.last_page || 1;
        const current = pagination.current_page || 1;

        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        // Start pages
        if (current <= 2) {
            return [1, 2, 3, "...", totalPages];
        }

        // End pages
        if (current >= totalPages - 1) {
            return [1, "...", totalPages - 2, totalPages - 1, totalPages];
        }

        // Middle pages: 1 ... previous current next ... last
        return [1, "...", current - 1, current, current + 1, "...", totalPages];
    };

    const { start, end } = getShowingRange();


    // const handleInvoiceDownload = async (transactionId) => {
    //     if (!transactionId) {
    //         toast.error("Transaction ID is missing.");
    //         return;
    //     }

    //     try {
    //         const response = await downloadInvoice(transactionId);

    //         const contentType = response?.headers?.["content-type"] || "application/pdf";
    //         const blob = new Blob([response.data], { type: contentType });
    //         const url = window.URL.createObjectURL(blob);

    //         const link = document.createElement("a");
    //         link.href = url;

    //         const contentDisposition = response?.headers?.["content-disposition"];
    //         let fileName = `invoice-${transactionId}.pdf`;

    //         if (contentDisposition) {
    //             const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
    //             if (fileNameMatch?.[1]) {
    //                 fileName = decodeURIComponent(fileNameMatch[1].replace(/"/g, ""));
    //             }
    //         }

    //         link.download = fileName;
    //         document.body.appendChild(link);
    //         link.click();
    //         link.remove();
    //         window.URL.revokeObjectURL(url);

    //         toast.success("Invoice downloaded successfully.");
    //     } catch (error) {
    //         console.error("Invoice download failed:", error);
    //         toast.error(error?.response?.data?.message || "Unable to download invoice.");
    //     }
    // };


    const handleInvoiceDownload = async (transactionId, documentType) => {
        if (!transactionId) {
            toast.error("Transaction ID is missing.");
            return;
        }

        if (!documentType) {
            toast.error("Document type not available.");
            return;
        }

        try {
            const response = await downloadInvoice(transactionId, documentType);

            const contentType =
                response?.headers?.["content-type"] || "application/pdf";

            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;

            // Default fallback filename
            let fileName = `${documentType}-${transactionId}.pdf`;

            const contentDisposition =
                response?.headers?.["content-disposition"];

            if (contentDisposition) {
                const match = contentDisposition.match(
                    /filename\*?=(?:UTF-8''|")?([^";]+)/i
                );
                if (match?.[1]) {
                    fileName = decodeURIComponent(match[1].replace(/"/g, ""));
                }
            }

            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

            toast.success(
                `${documentType === "credit-note" ? "Credit note" : "Invoice"} downloaded successfully.`
            );
        } catch (error) {
            // console.error("Download failed:", error);
            toast.error(
                error?.response?.data?.message ||
                "Unable to download document."
            );
        }
    };

    return (
        <>
            <div className="my-payments-page">
                <div className="my-payments-header py-4">
                    <div className="my-payments-heading-wrap">
                        {/* <h5 className="my-payments-title mb-1">My Payments</h5> */}
                        <h2 className="h4 mb-1 my-payments-title">Payments</h2>
                        <div className="text-muted small my-payments-subtitle">
                            Payment history. View invoices and credit notes under Transactions.
                        </div>
                    </div>

                    <div className="my-payments-header-search">
                        <InputGroup className="my-payments-search">
                            <InputGroup.Text className="bg-white">
                                <FontAwesomeIcon icon={faSearch} className="text-muted" />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ boxShadow: "none" }}
                            />
                        </InputGroup>
                    </div>
                </div>

                <Card className="border-0 shadow-sm rounded-4">
                    <Card.Body className="p-4">
                        <div className="my-payments-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div className="my-payments-list-title-wrap">
                                <h5 className="mb-1">All Transactions</h5>
                                {/* <div className="text-muted small">User payment history and invoice access</div> */}
                            </div>

                            {/* <Button
                type="button"
                style={ACTIVE_BUTTON_STYLE}
                className="my-payments-action-btn fw-semibold"
                onClick={() => toast.info("Export action will be available soon.")}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Export
              </Button> */}
                        </div>

                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0 my-payments-table">
                                <thead>
                                    <tr>
                                        <th>Sr No</th>
                                        <th>Date</th>
                                        <th>Transaction ID</th>
                                        <th>Course</th>
                                        <th className="text-end">Amount</th>
                                        <th className="text-center">Type</th>
                                        <th>Payment Method</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-center">Download</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={9} className="text-center text-muted py-5">
                                                Loading payments...
                                            </td>
                                        </tr>
                                    ) : transactions.length > 0 ? (
                                        transactions.map((item, index) => (
                                            <tr key={item.apiId ?? item.transactionId}>

                                                <td className="fw-semibold">{start + index}</td>
                                                <td className="text-muted">{formatDate(item.date)}</td>
                                                <td className="fw-semibold text-center">{item.transactionId || "N/A" || "-"}</td>
                                                <td className="fw-semibold">{item.course}</td>
                                                {/* <td className="text-end fw-semibold"> */}
                                                {/* <td
                                                    className={`text-end fw-bold ${item.type === "Credit" ? "text-success" : "text-danger"
                                                        }`}
                                                >
                                                    {item.type === "Credit" ? "+" : "-"}
                                                    {formatCurrency(item.amount)}</td> */}
                                                <td className="fw-semibold">{formatCurrency(item.amount)}</td>
                                                <td className="fw-semibold">{capitalize(item.type)}</td>
                                                <td>{item.paymentMethod}</td>
                                                <td className="text-center">
                                                    <Badge
                                                        className="rounded-pill d-inline-flex align-items-center justify-content-center"
                                                        style={{
                                                            ...getStatusBadgeStyle(item.status),
                                                            minWidth: "5.5rem",
                                                            height: "2rem",
                                                            padding: "0 12px",
                                                        }}
                                                    >
                                                        {formatStatus(item.status)}
                                                    </Badge>
                                                </td>
                                                {/* <td className="text-center">
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="p-0 text-decoration-none"
                                                        disabled={item.status !== "completed"}
                                                        onClick={() => handleInvoiceDownload(item.transactionId)}
                                                    >
                                                        <FontAwesomeIcon icon={faFileInvoice} />
                                                    </Button>
                                                </td> */}

                                                <td className="text-center">
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="p-0 text-decoration-none"
                                                        disabled={
                                                            item.status !== "completed" ||
                                                            !item?.documentAvailable ||
                                                            !item?.documentType
                                                        }
                                                        onClick={() =>
                                                            handleInvoiceDownload(
                                                                item.transactionId,
                                                                item.documentType
                                                            )
                                                        }
                                                        title={
                                                            item?.documentAvailable
                                                                ? `Download ${item.documentType}`
                                                                : "Document not available"
                                                        }
                                                    >
                                                        <FontAwesomeIcon icon={faFileInvoice} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={9} className="text-center text-muted py-5">
                                                No payments found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                            <div className="text-muted small">
                                Showing {start} to {end} of {pagination.total} entries
                            </div>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    disabled={pagination.current_page <= 1 || loading}
                                    onClick={() =>
                                        fetchPayments(
                                            Math.max(1, pagination.current_page - 1),
                                            pagination.per_page,
                                            search.trim()
                                        )
                                    }
                                    className="course-page-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </Button>

                                {getPageItems().map((item, index) =>
                                    item === "..." ? (
                                        <span key={`dots-${index}`} className="text-muted px-1">
                                            ...
                                        </span>
                                    ) : (
                                        <Button
                                            key={item}
                                            type="button"
                                            variant="outline-primary"
                                            disabled={loading}
                                            onClick={() => fetchPayments(item, pagination.per_page, search.trim())}
                                            className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""
                                                }`}
                                        >
                                            {item}
                                        </Button>
                                    )
                                )}

                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    disabled={pagination.current_page >= pagination.last_page || loading}
                                    onClick={() =>
                                        fetchPayments(
                                            Math.min(pagination.last_page, pagination.current_page + 1),
                                            pagination.per_page,
                                            search.trim()
                                        )
                                    }
                                    className="course-page-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </div>

            <style>{`
        .my-payments-page {
          width: 100%;
          padding-inline: 0;
        }

        .my-payments-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .my-payments-heading-wrap {
          min-width: 0;
          flex: 0 0 auto;
        }

        .my-payments-title {
          font-size: clamp(1.4rem, 2vw, 2rem);
          line-height: 1.2;
          word-break: break-word;
          white-space: nowrap;
        }

        .my-payments-subtitle {
          margin-top: 4px;
          word-break: break-word;
        }

        .my-payments-header-search {
          flex: 1 1 300px;
          max-width: 300px;
          margin-left: auto;
        }

        .my-payments-search {
          width: 100%;
        }

        .my-payments-search .input-group-text,
        .my-payments-search .form-control {
          min-height: 48px;
          height: 48px;
        }

        .my-payments-search .input-group-text {
          display: flex;
          align-items: center;
        }

        .my-payments-list-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: nowrap;
        }

        .my-payments-list-title-wrap {
          min-width: 0;
          flex: 1 1 auto;
          text-align: left;
        }

        .my-payments-list-title-wrap h5 {
          text-align: left;
        }

        .my-payments-action-btn {
          white-space: nowrap;
          min-height: 48px;
          padding-inline: 18px;
          margin-left: auto;
          flex: 0 0 auto;
        }

        .my-payments-page-btn {
          width: 2rem;
          height: 2rem;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .my-payments-page-btn svg {
          font-size: 0.95rem;
        }

        @media (max-width: 991.98px) {
          .my-payments-header {
            flex-wrap: wrap;
          }

          .my-payments-header-search {
            flex: 1 1 100%;
            max-width: none;
            order: 3;
          }

          .my-payments-search {
            width: 100%;
          }

          .my-payments-list-top {
            flex-wrap: wrap;
          }
        }

        @media (max-width: 767.98px) {
          .my-payments-header {
            align-items: flex-start;
            gap: 0.85rem;
          }

          .my-payments-header-search {
            order: 2;
            width: 100%;
          }

          .my-payments-search {
            width: 100%;
          }

          .my-payments-list-top {
            align-items: flex-start;
          }

          .my-payments-action-btn {
            width: auto;
          }

          .my-payments-search .input-group-text,
          .my-payments-search .form-control {
            min-height: 48px;
            height: 48px;
          }
        }

        @media (max-width: 575.98px) {
          .my-payments-header {
            padding-top: 0.5rem !important;
            padding-bottom: 1rem !important;
          }

          .my-payments-title {
            font-size: 1.35rem;
          }

          .my-payments-search .input-group-text,
          .my-payments-search .form-control {
            min-height: 42px;
            height: 42px;
          }

          .my-payments-search .form-control {
            font-size: 0.95rem;
          }

          .my-payments-action-btn {
            min-height: 42px;
            padding-inline: 14px;
            font-size: 0.92rem;
          }
        }
      `}</style>
        </>
    );
}